import React from 'react';
import styled from 'styled-components';
import { DataElementContext } from '@/page-components/common/DataElementContext';
import { processComponentProps } from '@/page-components/utils/processComponentProps';

//import './index.scss';

type HomeProps = {
  children: any;
  styleText: string;
  className: string;
  properties?: any;
};

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

const Home = (componentProps: HomeProps) => {
  let props = componentProps;

  const dataElementContext = React.useContext(DataElementContext);
  [props] = processComponentProps(props, dataElementContext);

  const [state, setState] = React.useState({
    selectedTimeRange: {
      metrics1: '7d',
      metrics2: '7d',
      metrics3: '7d',
      eventsTable1: '7d',
    },
    events: [
      {
        className: 'row-anim',
        data: [
          { label: 'Halloween Poking' },
          { label: 'Monetization' },
          { label: 'Churn prevention' },
          { label: 'Push' },
          { label: 'Found 20 users that ...' },
          { label: 'Executed', status: 'success' },
        ],
      },
      {
        className: 'row-anim',
        data: [
          { label: 'Football Master Promo' },
          { label: 'Retention' },
          { label: 'Activation' },
          { label: 'SMS' },
          { label: 'Found 12 online users' },
          { label: 'Missed', status: 'error' },
        ],
      },
      {
        className: 'row-anim',
        data: [
          { label: 'Copa Talento Futsal' },
          { label: 'Retention' },
          { label: 'Acquisition' },
          { label: 'Mail' },
          { label: 'Reasoning football event' },
          { label: 'Expired', status: 'error' },
        ],
      },
      {
        className: 'row-anim',
        data: [
          { label: 'Lorem Ipsum Dolor' },
          { label: 'Conversion' },
          { label: 'Retention' },
          { label: 'Mail' },
          { label: 'Found 12 online users' },
          { label: 'Executed', status: 'success' },
        ],
      },
    ],
  });

  React.useEffect(() => {
    let mounted = true;
    const tid = setInterval(() => {
      if (!mounted) return;

      // simulate data update
      setState((prevState) => {
        const events = [...prevState.events];
        // remove last element and add it to the beginning
        const lastEvent = events.pop();
        if (lastEvent) {
          events.unshift({ ...lastEvent });
          events[0].className = '';
        }

        setTimeout(() => {
          if (!mounted) return;

          // add animation class after a short delay
          setState((prevState2) => {
            const events2 = [...prevState2.events];
            events2[0] = { ...events2[0] };
            events2[0].className = 'row-anim';
            return {
              ...prevState2,
              events: events2,
            };
          });
        }, 100);

        return {
          ...prevState,
          events: events,
        };
      });
    }, 3000);

    return () => {
      clearInterval(tid);
      mounted = false;
    };
  }, []);

  const onTimeRangeChange = React.useCallback((e: React.MouseEvent<HTMLElement>) => {
    const metricsId = e?.currentTarget?.dataset?.id ?? null;
    const timeRangeValue = e?.currentTarget?.dataset?.value ?? null;

    if (metricsId && ['eventsTable1', 'metrics1', 'metrics2', 'metrics3'].includes(metricsId) && timeRangeValue) {
      setState((prevState) => ({
        ...prevState,
        selectedTimeRange: {
          ...prevState.selectedTimeRange,
          [metricsId]: timeRangeValue,
        },
      }));
    }
  }, []);

  const contextValue = React.useMemo(() => {
    const timeRange = [
      { label: '1D', value: '1d' },
      { label: '7D', value: '7d' },
      { label: '1M', value: '1m' },
      { label: '6M', value: '6m' },
      { label: '1Y', value: '1y' },
    ];

    return {
      data: {
        generalStats: {
          product: {
            value: 84,
            requiredActions: 0,
          },
          marketing: {
            value: 54,
            requiredActions: 3,
          },
          operations: {
            value: 91,
            requiredActions: 0,
          },
        },
        metrics: [
          {
            id: 'metrics1',
            type: 'with-graph-extended',
            title: 'New Players Joining Your Casino',
            subtitle: 'How many people are coming in and are they converting?',
            links: [
              {
                name: 'Registration',
                url: '#registrations',
                value: '200',
                change: '+4.5%',
                changeType: 'increase',
                vs: 'vs same day last week',
              },
              {
                name: 'FTDs',
                url: '#ftds',
                value: '124',
                change: '-0.4%',
                changeType: 'decrease',
                vs: 'vs same day last week',
              },
            ],
            timeRange: timeRange,
            selectedTimeRange: state.selectedTimeRange.metrics1,
            actions: {
              onTimeRangeChange,
            },
            graph: {
              type: 'line',
              data: {
                labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
                datasets: [
                  {
                    label: 'Registrations',
                    data: [30, 45, 28, 80, 99, 70, 50],
                    borderColor: '#803CFF',
                    backgroundColor: '#803CFF',
                    tension: 0.3,
                  },
                  {
                    label: 'FTDs',
                    data: [20, 35, 18, 45, 60, 33, 40],
                    borderColor: '#EC4899',
                    backgroundColor: '#EC4899',
                    tension: 0.3,
                  },
                ],
              },
              options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom' as const,
                    align: 'end' as const,
                    labels: {
                      boxWidth: 8,
                      boxHeight: 8,
                      useBorderRadius: true,
                      borderRadius: 4,
                    },
                  },
                },
              },
            },
          },
          {
            id: 'metrics2',
            type: 'no-graph',
            title: 'Money Flow',
            subtitle: 'The movement of real cash',
            links: [
              {
                name: 'Deposits',
                url: '#deposits',
                value: '$821K',
                change: '+8.5%',
                changeType: 'increase',
                vs: 'vs last month',
              },
            ],
            stats: [
              { name: 'Withdrawals', value: '$10K' },
              { name: 'Player Balance', value: '$12K' },
            ],
            timeRange: timeRange,
            selectedTimeRange: state.selectedTimeRange.metrics2,
            actions: {
              onTimeRangeChange,
            },
          },
          {
            id: 'metrics3',
            type: 'with-graph',
            title: 'GGR & Wagering Analysis',
            subtitle: 'Tracking the conversion from Handle to Gross Profit',
            links: [
              {
                name: 'GGR',
                url: '#ggr',
                value: '$621K',
                change: '+2.5%',
                changeType: 'increase',
                vs: 'vs last month',
              },
            ],
            stats: [
              { name: 'Stakes', value: '$5K' },
              { name: 'Wins', value: '$6K' },
            ],
            timeRange: timeRange,
            selectedTimeRange: state.selectedTimeRange.metrics3,
            actions: {
              onTimeRangeChange,
            },
            graph: {
              type: 'line',
              data: {
                labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
                datasets: [
                  {
                    label: 'GGR',
                    data: [3000, 5000, 7000, 8000, 9000, 10500, 12000],
                    borderColor: '#8070E9',
                    backgroundColor: '#8070E9',
                    tension: 0.3,
                  },
                  {
                    label: 'Stakes',
                    data: [2000, 3000, 4000, 5000, 6000, 7000, 8000],
                    borderColor: '#335C49',
                    backgroundColor: '#335C49',
                    tension: 0.3,
                  },
                  {
                    label: 'Wins',
                    data: [1000, 1500, 2000, 2500, 3000, 3500, 4000],
                    borderColor: '#0052AA',
                    backgroundColor: '#0052AA',
                    tension: 0.3,
                    borderDash: [3, 3],
                  },
                ],
              },
              options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom' as const,
                    align: 'end' as const,
                    labels: {
                      boxWidth: 8,
                      boxHeight: 8,
                      useBorderRadius: true,
                      borderRadius: 4,
                    },
                  },
                },
              },
            },
          },
        ],
        eventsTable: {
          id: 'eventsTable1',
          type: 'with-graph-extended',
          title: 'New Players Joining Your Casino',
          subtitle: 'How many people are coming in and are they converting?',
          timeRange: timeRange,
          selectedTimeRange: state.selectedTimeRange.eventsTable1,
          actions: {
            onTimeRangeChange,
          },
          tableHeader: [
            { label: 'OPERATION' },
            { label: 'CATEGORY' },
            { label: 'SCOPE' },
            { label: 'CHANNEL' },
            { label: 'DETAILS' },
            { label: 'STATUS' },
          ],
          tableRows: state.events,
        },
      },
      actions: {},
      parentContext: dataElementContext,
    };
  }, [dataElementContext, state.selectedTimeRange, onTimeRangeChange, state.events]);

  console.log('oh.module.home[contextValue]', contextValue);

  return (
    <ModuleElementDiv className={props.className ?? ''} $styleText={props.styleText}>
      <DataElementContext.Provider value={contextValue}>{componentProps.children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default Home;
